import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLangContext } from "../../providers/LangProvider/LangProviderContext";
import amountFormat from "../../utils/amountFormat";
import parseAccountName from "../../utils/parseAccountName";
import { openNotificationAction } from "../Notifications/notificationsActions";
import LiteTradeNotification from "./components/LiteTradeNotification/LiteTradeNotification";
import LiteTradeNotificationCloseButton from "./components/LiteTradeNotification/LiteTradeNotificationCloseButton";
import {
    LITE_TRADE_NOTIFICATION_STATUSES,
    LITE_TRADE_NOTIFICATION_TYPES
} from "./components/LiteTradeNotification/liteTradeNotificationConstants";
import LiteTradeNotificationTopUp from "./components/LiteTradeNotification/LiteTradeNotificationTopUp";
import {
    processLiteTradeOrderNotificationAction,
    processLiteTradePositionNotificationAction,
    processLiteTradeRejectedOrderNotificationAction,
    setLiteTradeAccountPortfolio
} from "./liteTradeActions.js";
import {
    LITE_TRADE_ACCOUNT_EVENT_TYPES,
    LITE_TRADE_DX_REJECT_CODES,
    LITE_TRADE_ORDER_STATUSES,
    MAP_LITE_TRADE_DX_REJECT_SUBSTRING_TO_REJECT_CODE
} from "./liteTradeConstants";
import { getLiteTradeAssetsBySymbol } from "./liteTradeSelectors";
import _ from "lodash";

const findLiteTradeRejectCode = (rejectReason) => Object.entries(MAP_LITE_TRADE_DX_REJECT_SUBSTRING_TO_REJECT_CODE)
    .find(([rejectSubstring]) => rejectReason.includes(rejectSubstring))?.[1] || '';

export const useLiteTradeAccountPortfolios = () => {
    const dispatch = useDispatch();
    const { getLangPath } = useLangContext();

    const processPortfolioNotifications = useCallback((portfolio) => {
        portfolio.orders.forEach(order => {
            if (order.status === LITE_TRADE_ORDER_STATUSES.COMPLETED) {
                dispatch(processLiteTradePositionNotificationAction({ portfolio, order }))
            } else if (order.status === LITE_TRADE_ORDER_STATUSES.REJECTED) {
                const execution = _.find(order.executions, {
                    finalStatus: true,
                    status: LITE_TRADE_ORDER_STATUSES.REJECTED
                }) || {};
                const rejectCode = findLiteTradeRejectCode(execution.rejectReason);
                let description, notificationChildren, notificationStatus, autoClose = true;
                if (rejectCode) {
                    description = getLangPath(`lite_version.notifications.reject_reason.${rejectCode}`, {symbol: order.instrument});
                    if (rejectCode === LITE_TRADE_DX_REJECT_CODES.NOT_ENOUGH_SPACE) {
                        notificationChildren = <LiteTradeNotificationTopUp/>;
                        notificationStatus = LITE_TRADE_NOTIFICATION_STATUSES.ATTENTION;
                        autoClose = false;
                    }
                }
                dispatch(processLiteTradeRejectedOrderNotificationAction({
                    autoClose,
                    portfolio,
                    order,
                    description,
                    notificationChildren,
                    notificationStatus,
                }));
            } else if ([
                LITE_TRADE_ORDER_STATUSES.CANCELED,
                LITE_TRADE_ORDER_STATUSES.WORKING,
            ].includes(order.status)) {
                dispatch(processLiteTradeOrderNotificationAction({ portfolio, order }));
            }
        })
    }, [dispatch, getLangPath]);

    const handleGetAccountPortfolio = useCallback((portfolio) => {
        processPortfolioNotifications(portfolio);
        dispatch(setLiteTradeAccountPortfolio(portfolio));
    }, [dispatch, processPortfolioNotifications]);

    return { handleGetAccountPortfolio }
};

export const useLiteTradeAccountEvents = () => {
    const dispatch = useDispatch();

    const handleEventLiquidation = useCallback((accountEvent) => {
        const accountType = parseAccountName(accountEvent.account).type;
        dispatch(openNotificationAction({
            content: <LiteTradeNotification accountType={accountType}
                                            type={LITE_TRADE_NOTIFICATION_TYPES.LIQUIDATION}
                                            description={`Your margin level is ${accountEvent.actualRiskLevel} and has breached the liquidation level`}/>,
            options: {
                autoClose: false,
                closeButton: LiteTradeNotificationCloseButton,
            }
        }));
    }, [dispatch]);

    const handleEventMarginCall = useCallback((accountEvent) => {
        const accountType = parseAccountName(accountEvent.account).type;
        dispatch(openNotificationAction({
            content: <LiteTradeNotification accountType={accountType}
                                            type={LITE_TRADE_NOTIFICATION_TYPES.MARGIN_CALL}
                                            description={`Your margin level is ${accountEvent.actualRiskLevel} and has breached the initial margin alert level. You
                may need to fund your account or close positions`}/>,
            options: {
                autoClose: false,
                closeButton: LiteTradeNotificationCloseButton,
            }
        }));
    }, [dispatch]);

    const handleGetAccountEvents = useCallback((accountEvent, timestamp) => {
        switch (accountEvent.eventType) {
            case LITE_TRADE_ACCOUNT_EVENT_TYPES.MARGIN_CALL:
                return handleEventMarginCall(accountEvent, timestamp);
            case LITE_TRADE_ACCOUNT_EVENT_TYPES.LIQUIDATION:
                return handleEventLiquidation(accountEvent, timestamp);
            default:
                return;
        }
    }, [handleEventLiquidation, handleEventMarginCall]);

    return { handleGetAccountEvents }
}
